import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, EMPTY, Observable, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {CliEmpAttributes} from '../../../models/models.interfaces';
import {ApiService, Data, DataListAndCount} from '../../../shared/api';
import {SearchOptions, SelectEmployee} from '../../../shared/employees/employees.model';
import {EmployeesService} from '../../../shared/employees/employees.service';
import {ModalService} from '../../../shared/modal';
import {User, UserService} from '../../../shared/user';
import {getInitials} from '../../../shared/utils/strings';
import {UserRightsService} from '../user-rights.service';

@Component({
  selector: 'gp-transfer-user-access-modal',
  templateUrl: './transfer-user-access-modal.component.html',
  styleUrls: ['./transfer-user-access-modal.component.scss']
})

export class TransferUserAccessModalComponent extends Destroyable implements OnInit, OnDestroy {

  constructor(private modalService: ModalService,
              private cd: ChangeDetectorRef,
              private employeesService: EmployeesService,
              private userService: UserService,
              private toastService: ToastrService,
              private translate: TranslateService,
              private api: ApiService,
              private userRightService: UserRightsService) {
    super();
  }

  @Input() public isExternalAdmin: boolean;
  @Input() public user: User;
  @Input() public restrictedConfidentialityTypes: string[] = [];

  public userList: SelectEmployee[] = [];
  public selectedUserIds: Set<number> = new Set<number>();
  public isUsersLoading = false;
  public selectedUserTags = [];
  public unSelectedUsers: Data<CliEmpAttributes>[] = [];
  public page = 0;
  public perPage = 25;
  public seeInactiveContracts = false;
  protected readonly toString = toString;

  private searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalService.modalClosed.emit('transfer-user-access');
  }

  public ngOnInit(): void {
    this.selectedUserIds.clear();

    this.searchTerm$.asObservable()
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap((searchTerm) => searchTerm.length ? this.fetchUsers(searchTerm) : of({items: {data: []}, total: 0})),
        tap((users: DataListAndCount<CliEmpAttributes>) => {
          this.unSelectedUsers = this.isExternalAdmin ?
            users?.items?.data.filter(user => (this.isUniqueUser(user.id) && !this.isCurrentUser(user.id)))
            : users?.items?.data.filter(user => (this.isUniqueUser(user.id) && !this.isCurrentUser(user?.cliEmpId)));

          this.userList = this.generateUserList(this.unSelectedUsers);

          this.isUsersLoading = false;
          this.cd.markForCheck();
        })
      ).subscribe();
  }

  public removeUser(index) {
    const removedUserTagId = this.selectedUserTags[index]?.id;
    this.selectedUserIds.delete(+removedUserTagId);

    const newSelectedUserTags = this.selectedUserTags.filter(user => user.id !== removedUserTagId);
    this.selectedUserTags = [...newSelectedUserTags];
  }

  public generateUserList(userList: Data<CliEmpAttributes>[]): SelectEmployee[] {
    return userList
      .map((element: Data<CliEmpAttributes>) => {
        const isChecked = false;
        return {
          id: element?.id,
          text: this.getSelectedUser(
            `${element?.attributes.lastName.toUpperCase()} ${element?.attributes.firstName}`,
            element?.id?.toString()),
          isChecked
        };
      });
  }

  public getSelectedUser(name: string, status: string): string {
    const parts = name.trim().split(/\s+/);
    const initials = getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
    const avatar = `<span class="gp-initials-avatar gp-avatar-md">${initials}</span>`;

    return `<div class="emp">
                <div class="emp-avatar">
                    ${avatar}
                </div>
                <div class="emp-info">
                    <div>${name}</div>
                    <small class="text-muted">${status}</small>
                </div>
            </div>`;
  }

  public fetchUsers(searchTerm): Observable<DataListAndCount<CliEmpAttributes>> {
    this.isUsersLoading = true;
    const options: SearchOptions = {
      search: encodeURIComponent(searchTerm?.trim()),
      page: this.page,
      orderBy: 'lastName',
      perPage: this.perPage
    };
    const confidentialityLevelExclusionFilter = `profile.confidentialityLevel=out=(${this.restrictedConfidentialityTypes.join(',')})`;
    return this.isExternalAdmin ? this.userService.getAllNonEmployeeUsers(options, confidentialityLevelExclusionFilter) :
      this.userService.getAllEmployeeUsers(options, confidentialityLevelExclusionFilter);
  }

  public selectUser(value: { id: number, value: boolean }): void {
    if (value.value) {
      this.selectedUserIds.add(value.id);
      this.userList = this.userList.filter(user => !this.selectedUserIds.has(user.id));
    }

    const newUserTag = this.unSelectedUsers
      .filter(user => user.id === value.id)
      .map(obj => {
        return {id: obj.id.toString(), name: `${obj?.attributes.lastName.toUpperCase()} ${obj?.attributes.firstName}`};
      });

    this.selectedUserTags = [...this.selectedUserTags, ...newUserTag];
  }

  public transferAccess(): void {
    let updateAccess$: Observable<HttpResponse<void>>;
    const queryParams = Array.from(this.selectedUserIds).map((user) => 'toUserId=' + user).join('&');

    if (this.isExternalAdmin) {
      updateAccess$ = this.userRightService.transferUserAccessByUserId(this.user.id, queryParams);
    } else {
      updateAccess$ = this.userRightService.transferUserAccess(this.user.id, queryParams);
    }

    updateAccess$
      .pipe(
        tap(() => {
          this.modalService.closeTopmostModal();
          this.toastService.success(this.translate.instant(`SETTINGS.user_rights.access_transferred_successfully`));
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          const errorMessage = `${this.translate.instant(`SETTINGS.user_rights.access_transfer_failed`)}: ${errorResponse?.error?.message}`;
          this.toastService.error(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  private isUniqueUser(userId) {
    return !this.selectedUserIds.has(userId);
  }

  public fetchUsersByKeyword(keyword: string): void {
    this.searchTerm$.next(keyword);
  }

  private isCurrentUser(userId: number) {
    return this.user.id === userId;
  }
}
